/**
 * Embed-context plumbing — surfaces "is this insight being rendered
 * inside the Lucent dashboard authoring/preview surface vs as a
 * customer-embedded view in someone else's product" to descendant
 * components.
 *
 * The signal comes from the source-fetch response's
 * `is_lucent_dashboard` field, which the server derives from the
 * verified embed-token's `sub` claim: tokens minted via a user JWT
 * (Lucent dashboard) resolve truthy; tokens minted via a client
 * service key (external embed) resolve falsy. The consumer page
 * can't override it.
 *
 * EmbedHost calls
 * `provide(IS_LUCENT_DASHBOARD_KEY, () => isLucentDashboard.value)`
 * once in setup. Descendants — currently the filter-render path,
 * potentially other dashboard-only affordances later — call
 * `useIsLucentDashboard()` and read `value()` to decide whether to
 * render.
 *
 * Why a thunk and not a `Ref<boolean>`? Same reasoning as
 * `print-mode.ts`: the signal latches on first source-fetch and
 * doesn't flip during a runner's life, but a thunk keeps consumers
 * reactive if they wrap the call in `computed(...)`.
 *
 * Keep this module dependency-free — it's imported by both EmbedHost
 * and the per-filter components, so a heavy import bloats the
 * runtime bundle proportionally.
 */

import { inject, type InjectionKey } from "vue";

/** Inject key for the is-Lucent-dashboard thunk. */
export const IS_LUCENT_DASHBOARD_KEY: InjectionKey<() => boolean> = Symbol(
  "lucent:is-lucent-dashboard",
);

/**
 * Read is-Lucent-dashboard from the nearest ancestor that provided
 * it. Defaults to `() => false` when no provider is found — a unit
 * test mounting a filter component in isolation, or a host that
 * pre-dates the source-fetch echo, both see the safer "treat as
 * external embed" fallback so dashboard-only affordances stay hidden
 * by default.
 */
export const useIsLucentDashboard = (): (() => boolean) =>
  inject(IS_LUCENT_DASHBOARD_KEY, () => false);

/** Where and as whom a component may call the embed API. */
export interface EmbedApi {
  apiUrl: string;
  token: string;
  insightId: string;
}

/**
 * Inject key for the embed-API thunk — for the few components that fetch
 * on their own (the document "View" panel), rather than through the
 * runner. A thunk because a token swap restarts the runner with new
 * credentials; read it at call time, never capture it. `null` when there
 * is no provider (a component mounted outside an EmbedHost, e.g. in a
 * test), which hides those affordances.
 */
export const EMBED_API_KEY: InjectionKey<() => EmbedApi | null> = Symbol("lucent:embed-api");

/** The embed-API thunk from the nearest EmbedHost, or one returning `null`. */
export const useEmbedApi = (): (() => EmbedApi | null) => inject(EMBED_API_KEY, () => null);
